/**
 * FarmersPulse 12-tool preview data.
 *
 * Every tool has:
 *  - meta: slug, stage, name, tagline, elevator, iconName, category
 *  - what[]: 3-6 bullets — plain-farmer language
 *  - how[]: 3-step "how it works" for the flow
 *  - mocks[]: 2 PhoneMock data blobs — capture + result screens
 *  - status: "shipping" | "building" | "designed"
 *  - deepLink: fpPath() destination when available (falls back to null)
 *  - category: "farm-management" | "marketing"  (drives the hub tabs)
 *
 * Marketing tools (13-24) live in ./farmerToolkitMarketing.js and are
 * concatenated onto TOOLKIT below.
 *
 * Never invent farmer numbers or brand names in mock data — everything
 * shown here is illustrative and prefixed with a light "Example" chip
 * on the preview page so farmers don't mistake it for their own data.
 */

import { MARKETING_TOOLS } from "./farmerToolkitMarketing";
import { TOOL_FEATURES } from "./farmerToolkitFeatures";
import { MARKETING_TOOL_FEATURES } from "./farmerToolkitMarketingFeatures";

const FARM_MANAGEMENT_TOOLS = [
  {
    slug: "profile-website",
    stage: 1,
    name: "Farm Profile & Website",
    iconName: "shop",
    tagline: "One farm — one story — one link.",
    elevator: "Build a farm profile and a public web page in about ten minutes. Add photos, opening hours, products, a WhatsApp button and a printable QR — hand it to any customer.",
    what: [
      "Simple 4-step wizard: farm identity, location, crops, livestock.",
      "Choose a template, drop in photos, publish.",
      "Auto-QR code + short link for flyers, packaging, farmers-market signs.",
      "Reads in 37 languages with a Listen button on every page.",
    ],
    how: [
      { title: "Set up your farm", body: "Tell us the name, where you are, and what you grow." },
      { title: "Pick a look", body: "Ten templates — from Rustic to Modern to Coastal." },
      { title: "Publish & share", body: "Your farm gets a link, a QR, and a WhatsApp button." },
    ],
    status: "shipping",
    deepLink: "/websites",
    mocks: [
      {
        eyebrow: "Set up my farm",
        title: "Step 3 · Your crops",
        subtitle: "Pick everything you grow this year — you can edit later.",
        blocks: [
          { kind: "chips", label: "Vegetables", options: ["Tomato", "Okra", "Chili", "Cabbage", "Onion"], active: "Tomato" },
          { kind: "chips", label: "Fruits",     options: ["Mango", "Banana", "Papaya", "Guava"], active: "Mango" },
          { kind: "chips", label: "Grains",     options: ["Rice", "Millet", "Maize"], active: "Rice" },
          { kind: "info",  text: "You can always add more from the Inventory tool." },
          { kind: "cta", label: "Next · Add photos", icon: "camera" },
        ],
      },
      {
        eyebrow: "Live farm page",
        title: "Green Valley Farm",
        subtitle: "Preview of your public web page — same on phone and desktop.",
        blocks: [
          { kind: "stat-row", items: [
            { label: "Products", value: "12" },
            { label: "Languages", value: "37" },
          ]},
          { kind: "list", title: "Fresh today", items: [
            { icon: "basket", title: "Roma tomatoes", sub: "Picked today · $2.35/kg", right: "New" },
            { icon: "basket", title: "Alphonso mango", sub: "Peak season · sold by tray" },
            { icon: "leaf",   title: "Curry leaves",  sub: "Bunches, no pesticide" },
          ]},
          { kind: "cta", label: "Get your QR code", icon: "sparkle" },
        ],
      },
    ],
  },

  {
    slug: "product-catalog",
    stage: 1,
    name: "Product & Availability Catalog",
    iconName: "basket",
    tagline: "One record. Feeds everything.",
    elevator: "Add a product once — it appears on your website, your inventory, your marketplace listing, and your buyer-match feed. Stop copy-pasting the same tomato four times.",
    what: [
      "Sub-1-minute add flow: photo, price, unit, quantity — done.",
      "Status chip on every card: Available now · Coming soon · Sold out.",
      "Single source of truth backed by inventory — no duplicate rows.",
      "Fans out automatically to Website, Marketplace, Wholesale sheet.",
    ],
    how: [
      { title: "Snap or pick a photo", body: "The photo goes into your gallery and your product card." },
      { title: "Add price + unit", body: "$2.35 per kg, or 'call for price' if you prefer." },
      { title: "Choose where it appears", body: "Farm page · marketplace · wholesale — all three by default." },
    ],
    status: "building",
    deepLink: "/inventory",
    mocks: [
      {
        eyebrow: "Add product",
        title: "Roma tomato",
        subtitle: "One record — publish everywhere in one tap.",
        blocks: [
          { kind: "gallery", n: 3 },
          { kind: "input", label: "Price",    value: "$2.35 per kg", icon: "coins" },
          { kind: "input", label: "On hand",  value: "120 kg (Field 3)", icon: "package" },
          { kind: "chips", label: "Visibility", options: ["Farm page", "Marketplace", "Wholesale"], active: "Farm page" },
          { kind: "cta", label: "Publish everywhere", icon: "send" },
        ],
      },
      {
        eyebrow: "My catalog",
        title: "6 products live",
        subtitle: "Every card carries a live availability chip.",
        blocks: [
          { kind: "list", items: [
            { icon: "basket", title: "Roma tomato",  sub: "$2.35/kg · 120 kg", right: "Available" },
            { icon: "basket", title: "Alphonso mango", sub: "Tray · seasonal", right: "Available" },
            { icon: "leaf",   title: "Curry leaves", sub: "Bunches", right: "Low" },
            { icon: "basket", title: "Bitter gourd", sub: "Kg", right: "Sold out" },
            { icon: "plant",  title: "Banana",       sub: "Bunch", right: "Coming soon" },
          ]},
          { kind: "info", text: "Marketplace picked up 4 new listings today.", tone: "ok" },
        ],
      },
    ],
  },

  {
    slug: "farm-map",
    stage: 1,
    name: "Farm Map & Field Manager",
    iconName: "map",
    tagline: "Draw your fields. Attach everything.",
    elevator: "Walk your boundaries with your phone in your pocket — the app records the polygon. Every note, task, cost and photo attaches to the field it happened on.",
    what: [
      "Draw-on-map polygon editor, or walk the boundary GPS mode.",
      "Automatic area + perimeter, in acres, hectares or cents.",
      "Every field owns its crops, tasks, photos, expenses, harvests.",
      "Export a clean GeoJSON — for co-ops, insurance, or subsidies.",
    ],
    how: [
      { title: "Add a field", body: "Name it (e.g. 'Field 3'), pick a colour." },
      { title: "Draw or walk", body: "Sketch the polygon on the map, or press Walk and stroll the edge." },
      { title: "Attach records", body: "Everything you log from now on can point to this field." },
    ],
    status: "designed",
    deepLink: "/farms",
    mocks: [
      {
        eyebrow: "Draw a field",
        title: "Field 3 · Tomato block",
        subtitle: "Walk the edge or tap points on the map.",
        blocks: [
          { kind: "info", text: "Press start · walk the boundary · tap stop when you return." },
          { kind: "stat-row", items: [
            { label: "Area",      value: "1.24 ac" },
            { label: "Perimeter", value: "482 m" },
          ]},
          { kind: "chips", label: "Method", options: ["Walk", "Tap on map", "Import"], active: "Walk" },
          { kind: "cta", label: "Start walking boundary", icon: "map" },
        ],
      },
      {
        eyebrow: "Field detail",
        title: "Everything on Field 3",
        subtitle: "Notes, tasks, costs, harvests — all attached to this polygon.",
        blocks: [
          { kind: "kv-grid", items: [
            { label: "Current crop", value: "Roma tomato" },
            { label: "Planted",      value: "12 May" },
            { label: "Est. harvest", value: "18 Aug" },
            { label: "Cost so far",  value: "$412" },
          ]},
          { kind: "list", title: "Recent activity", items: [
            { icon: "leaf",   title: "Compost applied", sub: "2 tractor loads · today" },
            { icon: "bug",    title: "Aphid observed",  sub: "Eastern edge · yesterday" },
            { icon: "basket", title: "Harvest logged",  sub: "38 kg · 3 days ago" },
          ]},
          { kind: "cta", label: "Export GeoJSON", icon: "sparkle" },
        ],
      },
    ],
  },

  {
    slug: "voice-diary",
    stage: 1,
    name: "Voice-Powered Field Diary",
    iconName: "microphone",
    tagline: "Talk to the farm. It writes the record.",
    elevator: "Speak what happened in the field — the app turns it into a structured record with the field, activity, quantity and follow-up already filled in. Your original recording is always preserved.",
    what: [
      "Tap once, speak in your language, get a structured entry back.",
      "Works offline — syncs later when you have signal.",
      "AI extracts field · activity · quantity · pest observed · follow-up.",
      "Original voice + text stay attached — review before saving.",
    ],
    how: [
      { title: "Press & speak", body: "'Applied two tractor loads of compost to Field 3. Aphids on the east edge.'" },
      { title: "AI extracts details", body: "It fills in field, activity, quantity, observation, next step." },
      { title: "Confirm & save", body: "Edit anything that looks off, then save. Original recording is kept." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Field diary",
        title: "Speak or type what happened",
        subtitle: "Works offline. Your original recording is preserved.",
        blocks: [
          { kind: "chips", options: ["Works offline"], active: "Works offline" },
          { kind: "textarea", label: "New field record", value: "Applied two tractor loads of compost to Field 3. Tomato plants look healthy. Aphids seen along the eastern edge." },
          { kind: "input", label: "Field", value: "Field 3 · Tomatoes", icon: "map" },
          { kind: "input", label: "Time",  value: "Today · 8:40 AM", icon: "sun" },
          { kind: "gallery", n: 3 },
          { kind: "cta", label: "Structure my record", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Structured record",
        title: "Draft for review",
        subtitle: "7 details extracted — confirm anything that looks off.",
        badge: "AI draft · review",
        blocks: [
          { kind: "kv-grid", items: [
            { label: "Activity", value: "Compost applied" },
            { label: "Quantity", value: "2 tractor loads" },
            { label: "Follow-up", value: "Scout in 2 days" },
          ]},
          { kind: "list", title: "Extracted", items: [
            { icon: "check",   title: "Field 3 · Tomatoes",  sub: "Linked to the current crop" },
            { icon: "check",   title: "Soil amendment",       sub: "Compost — weight optional" },
            { icon: "bug",     title: "Aphid observation",    sub: "Eastern edge · not yet an outbreak" },
          ]},
          { kind: "info", text: "Original recording preserved — tap to listen." },
        ],
      },
    ],
  },

  {
    slug: "inventory-surplus",
    stage: 2,
    name: "Inventory & Surplus Manager",
    iconName: "package",
    tagline: "Nothing forgotten. Nothing wasted.",
    elevator: "Track produce, feed, seed, tools, vehicles — one universal record. Set expiry alerts, watch surplus risk, one-tap donate to a local food bank when a crop peaks.",
    what: [
      "One record type flexes across produce, tools, vehicles, farm-stay furniture.",
      "Configurable thresholds: critical · low · reorder · overstock.",
      "Attention Center rolls up everything expiring, low, or needing repair.",
      "One-tap surplus routing to FoodPulse, discount, animal feed, or processing.",
    ],
    how: [
      { title: "Add anything", body: "Universal item — from a bag of feed to a tractor." },
      { title: "Set thresholds", body: "'Tell me when tomatoes drop below 20 kg' — done." },
      { title: "Reroute surplus", body: "One tap donates, discounts, or converts a bumper batch." },
    ],
    status: "shipping",
    deepLink: "/inventory",
    mocks: [
      {
        eyebrow: "Attention centre",
        title: "3 items need you today",
        subtitle: "Roll-up of expiring, low, and needs-repair.",
        blocks: [
          { kind: "stat-row", items: [
            { label: "Expiring", value: "5" },
            { label: "Low stock", value: "3" },
          ]},
          { kind: "list", items: [
            { icon: "warning", title: "Roma tomatoes",  sub: "Expiring in 2 days · 40 kg", right: "Route" },
            { icon: "warning", title: "Compost",         sub: "Low stock · reorder soon", right: "Order" },
            { icon: "warning", title: "Tractor sprayer", sub: "Needs repair · logged 3 days ago", right: "Book" },
          ]},
          { kind: "cta", label: "Open attention centre", icon: "sparkle" },
        ],
      },
      {
        eyebrow: "Reroute surplus",
        title: "40 kg tomatoes — pick a home",
        subtitle: "Same tap sends the batch and updates the ledger.",
        blocks: [
          { kind: "list", items: [
            { icon: "handshake", title: "Local food bank",   sub: "River City Pantry · 6 km", right: "Donate" },
            { icon: "shop",      title: "Discount shelf",    sub: "Show 30% off on farm page", right: "Discount" },
            { icon: "leaf",      title: "Animal feed swap",  sub: "Willow Farm accepts today", right: "Send" },
            { icon: "package",   title: "Preserve partner",  sub: "Sunrise saucery · 8 km",    right: "Refer" },
          ]},
          { kind: "info", text: "Every routing logs a ledger entry so your books stay clean.", tone: "ok" },
        ],
      },
    ],
  },

  {
    slug: "weather-to-action",
    stage: 2,
    name: "Weather-to-Action Assistant",
    iconName: "sun",
    tagline: "Forecast in. Farmer action out.",
    elevator: "The forecast is only useful when it becomes a decision. This tool turns rain, wind, frost and heat into plain-language 'do this today' cards with source, confidence and reasoning shown up front.",
    what: [
      "Reads Open-Meteo for your farm's exact lat/lon.",
      "Twelve rule types: irrigation · harvest · spray · frost · heat · wind · hail · livestock shelter · spray window · pest risk · soil workability.",
      "Every card shows source, timestamp, confidence, reasoning.",
      "Applies to your current crops only — not generic advice.",
    ],
    how: [
      { title: "Pick a farm", body: "Uses your registered farm's coordinates automatically." },
      { title: "Read today's cards", body: "'Hold spraying until Thursday · winds 22 km/h Wednesday PM.'" },
      { title: "Log the action", body: "Mark cards done — your diary keeps the record." },
    ],
    status: "shipping",
    deepLink: "/weather",
    mocks: [
      {
        eyebrow: "Today · Field 3",
        title: "3 weather actions for you",
        subtitle: "Every card shows source, confidence and reasoning.",
        blocks: [
          { kind: "list", items: [
            { icon: "warning", title: "Hold spraying till Thu",  sub: "Wind 22 km/h Wed PM · confidence 88%", right: "Skip" },
            { icon: "sun",     title: "Deep-water tomatoes today", sub: "Heat spike · 34°C forecast Fri", right: "Do" },
            { icon: "leaf",    title: "Cover cabbage seedlings", sub: "Frost risk 2°C · 5AM Sunday", right: "Do" },
          ]},
          { kind: "info", text: "Sources: Open-Meteo · your Field 3 crop record.", tone: "ok" },
        ],
      },
      {
        eyebrow: "Action detail",
        title: "Hold spraying till Thursday",
        subtitle: "Why we're recommending this, so you can decide.",
        blocks: [
          { kind: "kv-grid", items: [
            { label: "Rule",       value: "Spray window" },
            { label: "Wind Wed",   value: "22 km/h · 6 PM" },
            { label: "Confidence", value: "88%" },
            { label: "Applies to", value: "Roma tomato · Field 3" },
          ]},
          { kind: "info", text: "Reasoning: drift risk high above 15 km/h — recommendation revised at 6 AM local." },
          { kind: "cta", label: "Mark handled", icon: "check" },
        ],
      },
    ],
  },

  {
    slug: "crop-planning",
    stage: 2,
    name: "Crop Planning & Calendar",
    iconName: "plant",
    tagline: "Plan the season. Not just the day.",
    elevator: "Location-aware planting calendar for 24+ crops, succession-planting schedules, yield and labour estimates. Compare two crops side by side before you commit an acre.",
    what: [
      "Recommends crops for your latitude, hemisphere, month.",
      "Succession scheduling: batch 1 / batch 2 / batch 3 with nursery + transplant dates.",
      "Expected yield per acre, plus a rough labour estimate.",
      "Editable overrides that persist and feed next year's plan.",
    ],
    how: [
      { title: "Tell us where + when", body: "Farm location + this planting window." },
      { title: "See the recommendations", body: "Ranked list with duration, yield and season fit." },
      { title: "Lock in a plan", body: "Adds dates to your calendar and tasks to your diary." },
    ],
    status: "building",
    deepLink: "/plan-what-to-plant",
    mocks: [
      {
        eyebrow: "What to plant",
        title: "Kerala · this week",
        subtitle: "Ranked for your climate window and remaining season.",
        blocks: [
          { kind: "list", items: [
            { icon: "plant", title: "Okra",        sub: "60 days · 2.4 t/ac", right: "Plant now" },
            { icon: "plant", title: "Long beans",  sub: "80 days · 1.8 t/ac", right: "Plant now" },
            { icon: "plant", title: "Bitter gourd",sub: "70 days · 1.2 t/ac", right: "Next week" },
            { icon: "plant", title: "Amaranth",    sub: "40 days · 1.6 t/ac", right: "Anytime" },
          ]},
          { kind: "chips", label: "Filter", options: ["Under 60 days", "Rain-fed", "High yield"], active: "Under 60 days" },
        ],
      },
      {
        eyebrow: "Succession plan",
        title: "Roma tomato · 3 batches",
        subtitle: "Stagger by 14 days for a steady harvest.",
        blocks: [
          { kind: "list", items: [
            { icon: "plant",  title: "Batch 1 · nursery Apr 12", sub: "Transplant May 4 · harvest Jul 6" },
            { icon: "plant",  title: "Batch 2 · nursery Apr 26", sub: "Transplant May 18 · harvest Jul 20" },
            { icon: "plant",  title: "Batch 3 · nursery May 10", sub: "Transplant Jun 1 · harvest Aug 3" },
          ]},
          { kind: "stat-row", items: [
            { label: "Total yield",  value: "7.2 t" },
            { label: "Labour days",  value: "38" },
          ]},
          { kind: "cta", label: "Add to my calendar", icon: "check" },
        ],
      },
    ],
  },

  {
    slug: "pest-disease",
    stage: 2,
    name: "Pest & Disease Assistant",
    iconName: "bug",
    tagline: "Collect evidence. Read possibilities. Act safely.",
    elevator: "A structured symptom form and photo capture that shows ranked possibilities with confidence — always framed as 'possible identification, not confirmed diagnosis'. It never prescribes pesticide rates.",
    what: [
      "Crop · plant part · growth stage · distribution — structured form.",
      "Photos with a scouting checklist ('leaf undersides, ten plants across the edge').",
      "Ranked possibilities with confidence chips and short reasoning.",
      "'Farmer confirmation required' — never framed as a diagnosis.",
    ],
    how: [
      { title: "Photograph the problem", body: "Three shots · one wide, one close, one underside." },
      { title: "Describe what you see", body: "Structured form. No open-ended guessing." },
      { title: "Review possibilities", body: "Ranked list. Tap for scouting steps and next actions." },
    ],
    status: "building",
    deepLink: "/pest-heatmap",
    mocks: [
      {
        eyebrow: "Pest diagnosis",
        title: "Check a crop problem",
        subtitle: "Collect evidence first — no guesses.",
        badge: "Farmer confirmation required",
        blocks: [
          { kind: "gallery", n: 3 },
          { kind: "input", label: "Crop",       value: "Tomato",        icon: "plant" },
          { kind: "input", label: "Plant part", value: "Young leaves",  icon: "leaf" },
          { kind: "textarea", label: "What do you see?", value: "Leaves curling. Small green insects under leaves. Sticky surface." },
          { kind: "chips", label: "Distribution", options: ["Field edge", "Scattered", "Whole field"], active: "Field edge" },
          { kind: "cta", label: "Analyse evidence", icon: "magnify" },
        ],
      },
      {
        eyebrow: "Crop-health assessment",
        title: "Aphids · top possibility",
        subtitle: "Not a confirmed diagnosis. Scout further before acting.",
        badge: "Not a confirmed diagnosis",
        blocks: [
          { kind: "stat-row", items: [
            { label: "Top possibility", value: "Aphids" },
            { label: "Confidence",      value: "82%" },
          ]},
          { kind: "list", title: "Ranked possibilities", items: [
            { icon: "bug",     title: "Aphid activity",   sub: "Insects, leaf curl, sticky align", right: "82%" },
            { icon: "bug",     title: "Whitefly activity",sub: "Check for white adults",           right: "31%" },
            { icon: "warning", title: "Leaf-curl virus",  sub: "Only if symptoms persist w/o insects", right: "18%" },
          ]},
          { kind: "list", title: "Do this next", items: [
            { icon: "camera",  title: "Photograph leaf undersides" },
            { icon: "magnify", title: "Scout 10 plants across the edge" },
            { icon: "handshake", title: "Ask an adviser if damage expands" },
          ]},
        ],
      },
    ],
  },

  {
    slug: "orders-delivery",
    stage: 3,
    name: "Orders, Pickup & Delivery",
    iconName: "shop",
    tagline: "Enquiry → quote → order → out the door.",
    elevator: "A clean order lifecycle for direct-to-customer and wholesale — pickup or delivery, cash or card. Runs alongside the marketplace so buyers can self-serve the popular boxes.",
    what: [
      "Enquiry → quote → order flow, one direction, one lifecycle.",
      "Pickup slots, delivery routes, cash-on-collection.",
      "Card via Stripe (global) or Razorpay (India) — or QR-only mode.",
      "Every order posts a ledger entry to Farm Finance automatically.",
    ],
    how: [
      { title: "Buyer sends enquiry", body: "From your farm page, marketplace listing, or RFQ." },
      { title: "You send a quote", body: "One tap converts enquiry to a priced order." },
      { title: "Fulfil & log", body: "Pickup or delivery, paid or unpaid — everything reconciles." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Today's orders",
        title: "4 orders · $268 committed",
        subtitle: "Two pickups this afternoon, two deliveries tomorrow.",
        blocks: [
          { kind: "list", items: [
            { icon: "shop", title: "River City School",  sub: "180 kg Roma · Fri pickup", right: "$423" },
            { icon: "shop", title: "Basil Bistro",       sub: "20 kg mixed · Today 4 PM", right: "$68" },
            { icon: "shop", title: "Green Grocer Co-op", sub: "6 crates · Sat delivery",  right: "$142" },
          ]},
          { kind: "stat-row", items: [
            { label: "Awaiting quote", value: "3" },
            { label: "Ready to ship",  value: "2" },
          ]},
        ],
      },
      {
        eyebrow: "Order detail",
        title: "Basil Bistro · 20 kg mixed",
        subtitle: "Everything from enquiry to receipt in one place.",
        blocks: [
          { kind: "kv-grid", items: [
            { label: "Fulfilment",  value: "Pickup today · 4 PM" },
            { label: "Payment",     value: "On collection · QR" },
            { label: "Subtotal",    value: "$68.00" },
            { label: "Contact",     value: "Chef Miguel" },
          ]},
          { kind: "list", title: "What's in the box", items: [
            { icon: "basket", title: "Roma tomato",   sub: "12 kg · $2.35/kg", right: "$28.20" },
            { icon: "leaf",   title: "Curry leaves",  sub: "5 bunches",       right: "$5.00" },
            { icon: "basket", title: "Baby bok choy", sub: "3 kg · $11.60/kg", right: "$34.80" },
          ]},
          { kind: "cta", label: "Mark ready for pickup", icon: "check" },
        ],
      },
    ],
  },

  {
    slug: "ai-marketing",
    stage: 3,
    name: "AI Farm Marketing",
    iconName: "sparkle",
    tagline: "Drafts you approve. Never facts you didn't state.",
    elevator: "Templates for weekly emails, product descriptions, social posts and market reminders. Every draft cites the farm record it used and asks for your approval before publish. Never invents prices or certifications.",
    what: [
      "Eight templates: weekly email · product post · social · welcome · seasonal · reminder · review request · abandoned-order.",
      "Segments: past customers · nearby buyers · restaurants · CSA members.",
      "Fact-check panel shows which farm records the draft used.",
      "Approval-required — nothing publishes without you tapping Send.",
    ],
    how: [
      { title: "Pick a template", body: "Weekly newsletter, social post, or product description." },
      { title: "Review the draft", body: "See exactly which harvest, price and photo it pulled from." },
      { title: "Approve & send", body: "Fan-out to email · website · social · WhatsApp." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Weekly email draft",
        title: "This week at Green Valley",
        subtitle: "Sourced from your inventory + Fresh Today log.",
        badge: "Approval required",
        blocks: [
          { kind: "textarea", label: "Preview text", value: "Roma tomatoes are peaking, curry leaves are back, and Saturday's farm stand opens at 8 AM. Reserve a box before Friday noon." },
          { kind: "list", title: "Facts I used", items: [
            { icon: "check", title: "120 kg Roma tomatoes in stock", sub: "Inventory · today 6:20 AM" },
            { icon: "check", title: "Curry leaves back in season",    sub: "Fresh Today log · yesterday" },
            { icon: "check", title: "Farm stand opens Sat 8 AM",      sub: "Your public hours" },
          ]},
          { kind: "chips", label: "Send to", options: ["Past customers", "Nearby", "Restaurants"], active: "Past customers" },
        ],
      },
      {
        eyebrow: "Fan-out",
        title: "Approve to publish",
        subtitle: "Four channels — you approve once, we do the rest.",
        blocks: [
          { kind: "list", items: [
            { icon: "envelope", title: "Email · 218 recipients", sub: "Past customers segment", right: "Send" },
            { icon: "shop",     title: "Farm page banner",       sub: "Homepage · until Sunday", right: "Post" },
            { icon: "send",     title: "WhatsApp broadcast",     sub: "Regulars group · 46 numbers", right: "Send" },
            { icon: "sparkle",  title: "Social post",            sub: "Instagram + Facebook",     right: "Publish" },
          ]},
          { kind: "info", text: "Nothing is scheduled until you approve.", tone: "ok" },
        ],
      },
    ],
  },

  {
    slug: "buyer-marketplace",
    stage: 3,
    name: "Buyer Request Marketplace",
    iconName: "handshake",
    tagline: "Big buyers post the need. Farms match it — alone or together.",
    elevator: "Verified restaurants, schools and grocers post exactly what they need. Farms respond alone or combine supply with nearby farms to hit the volume. Contact details stay private until terms are accepted.",
    what: [
      "Verified-buyer flag with a lightweight vetting badge.",
      "Group-supply matching — three farms → one 180 kg/week contract.",
      "Anonymous chat until both sides accept the terms.",
      "Auto-conversion to an order the moment terms are signed.",
    ],
    how: [
      { title: "Buyer posts a need", body: "'180 kg Roma tomatoes weekly · Sep–Oct · school district.'" },
      { title: "Farms respond", body: "Supply alone or join a farmer group." },
      { title: "Terms accepted → order", body: "Contact opens, delivery is scheduled, ledger updates." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Buyer request",
        title: "Respond to River City School",
        subtitle: "180 kg Roma tomatoes weekly · Sep 8 – Oct 20.",
        badge: "Verified buyer",
        blocks: [
          { kind: "input", label: "Quantity you can supply weekly", value: "80 kg", icon: "basket" },
          { kind: "stat-row", items: [
            { label: "Price per kg", value: "$2.35" },
            { label: "Start date",   value: "Sep 8" },
          ]},
          { kind: "chips", label: "Fulfilment choice", options: ["Supply alone", "Join farmer group"], active: "Join farmer group" },
          { kind: "textarea", label: "Note to buyer", value: "Harvested within 24 hours. Reusable crates available." },
          { kind: "info", text: "Your direct contact stays private until terms are accepted.", tone: "ok" },
          { kind: "cta", label: "Submit contribution", icon: "send" },
        ],
      },
      {
        eyebrow: "Supply match · Roma tomatoes",
        title: "Group at 190 kg / target 180 kg",
        subtitle: "Weighted offer — nobody left short.",
        badge: "Target reached",
        blocks: [
          { kind: "stat-row", items: [
            { label: "Buyer demand", value: "180 kg/wk" },
            { label: "Committed",    value: "190 kg/wk" },
          ]},
          { kind: "list", title: "Farmer contributions", items: [
            { icon: "handshake", title: "Green Valley", sub: "80 kg", right: "$2.35" },
            { icon: "handshake", title: "Sunrise Acres", sub: "60 kg", right: "$2.30" },
            { icon: "handshake", title: "Willow Farm",   sub: "50 kg", right: "$2.40" },
          ]},
          { kind: "info", text: "Group quote ready · weighted $2.35/kg · 10 kg buffer covers a bad week." },
        ],
      },
    ],
  },

  {
    slug: "profitability",
    stage: 3,
    name: "Crop Cost & Profitability",
    iconName: "chart",
    tagline: "What made money. What lost money. What to try.",
    elevator: "Costs allocated by crop, field, acre, season and sales channel. Break-even, gross margin, return per acre — plus a plain-language 'what to test next season' recommendation drawn from your own record.",
    what: [
      "Cost allocation by crop, field, acre, season and channel.",
      "Break-even, gross margin, return-per-acre calculators.",
      "Channel comparison: farm shop vs wholesale vs CSA vs school buyer.",
      "'What to test next season' notes based on your own numbers.",
    ],
    how: [
      { title: "Log costs and harvests", body: "You already do this in Finance and Harvests." },
      { title: "Pick a crop + season", value: "Roma tomato · summer 2026 · Field 3." },
      { title: "Read the story", body: "Where the margin came from, what to change." },
    ],
    status: "designed",
    deepLink: null,
    mocks: [
      {
        eyebrow: "Roma tomato · summer",
        title: "Gross margin · $1,412",
        subtitle: "1.24 acre · Field 3 · one 12-week season.",
        blocks: [
          { kind: "stat-row", items: [
            { label: "Revenue",      value: "$2,684" },
            { label: "Cost",         value: "$1,272" },
          ]},
          { kind: "kv-grid", items: [
            { label: "Break-even",     value: "$1.11 / kg" },
            { label: "Sold at (avg)",  value: "$2.35 / kg" },
            { label: "Return per acre", value: "$1,138" },
          ]},
          { kind: "list", title: "Where the money came from", items: [
            { icon: "shop",      title: "Farm shop",       sub: "48% of revenue", right: "$1,288" },
            { icon: "handshake", title: "School district", sub: "31% · steady",   right: "$832" },
            { icon: "shop",      title: "Wholesale",       sub: "21% · lower rate", right: "$564" },
          ]},
        ],
      },
      {
        eyebrow: "Test next season",
        title: "3 changes worth trying",
        subtitle: "Based on this season's own numbers.",
        blocks: [
          { kind: "list", items: [
            { icon: "sparkle", title: "Shift 15% more to school district", sub: "Higher per-kg net · steady demand" },
            { icon: "sparkle", title: "Second batch on 14-day succession",  sub: "Cover the mid-August gap" },
            { icon: "sparkle", title: "Test drip irrigation on Field 3",    sub: "Cost estimate paid back in 2 seasons" },
          ]},
          { kind: "info", text: "Nothing above changes automatically — you decide what to run next year." },
        ],
      },
    ],
  },
];

export const TOOLKIT = [
  ...FARM_MANAGEMENT_TOOLS.map((t) => ({
    ...t,
    category: t.category || "farm-management",
    features: TOOL_FEATURES[t.slug] || null,
  })),
  ...MARKETING_TOOLS.map((t) => ({
    ...t,
    features: MARKETING_TOOL_FEATURES[t.slug] || null,
  })),
];

export const CATEGORIES = [
  { key: "farm-management", label: "Farm Management", tagline: "Grow it. Manage it." },
  { key: "marketing",       label: "Marketing Suite", tagline: "Sell it. Share it." },
];

export const STAGES = [
  { n: 1, name: "Digital foundation", description: "Farmers get online — profile, catalog, map and voice diary." },
  { n: 2, name: "Daily intelligence", description: "Inventory, weather, planning and diagnosis — every day, one glance." },
  { n: 3, name: "Measurable revenue", description: "Orders, marketing, marketplace, profitability — turn the farm into a business." },
];

export function toolBySlug(slug) {
  return TOOLKIT.find((t) => t.slug === slug) || null;
}
